import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Trash2, ShieldAlert, Copy, RotateCw, Plus,
  ExternalLink, AlertTriangle, TrendingUp, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

function severityColor(rating) {
  const s = (rating || "").toLowerCase();
  if (s === "critical") return "bg-red-100 text-red-800";
  if (s === "high") return "bg-orange-100 text-orange-800";
  if (s === "medium") return "bg-amber-100 text-amber-800";
  if (s === "low") return "bg-lime-100 text-lime-800";
  return "bg-zinc-100 text-zinc-700";
}

export default function ClientWordfencePage() {
  const { clientId } = useParams();
  const [webhook, setWebhook] = useState(null);
  const [software, setSoftware] = useState([]);
  const [matches, setMatches] = useState([]);
  const [events, setEvents] = useState([]);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masterState, setMasterState] = useState(null);
  const [newSw, setNewSw] = useState({ slug: "", type: "plugin", version: "", name: "", property_url: "" });
  const [busy, setBusy] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wh, sw, mt, ev, ms] = await Promise.all([
        api.get(`/wordfence/clients/${clientId}/webhook`),
        api.get(`/wordfence/clients/${clientId}/software`),
        api.get(`/wordfence/clients/${clientId}/matches`),
        api.get(`/wordfence/clients/${clientId}/events`),
        api.get(`/wordfence/master`).catch(() => ({ data: null })),
      ]);
      setWebhook(wh.data);
      setSoftware(sw.data.items || []);
      setMatches(mt.data.matches || []);
      setEvents(ev.data.events || []);
      setDaily(ev.data.daily || []);
      setMasterState(ms.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Wordfence data");
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const copyWebhook = async () => {
    if (!webhook?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(webhook.webhook_url);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Copy failed — select the URL manually");
    }
  };

  const regenerateWebhook = async () => {
    if (!window.confirm("Regenerate the webhook secret? The old URL will stop working immediately — update Wordfence with the new URL after this.")) return;
    setBusy("regen");
    try {
      const { data } = await api.post(
        `/wordfence/clients/${clientId}/webhook/regenerate`);
      setWebhook(data);
      toast.success("New webhook URL generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    } finally { setBusy(null); }
  };

  const addSoftware = async () => {
    if (!newSw.slug.trim()) { toast.error("Enter a plugin/theme slug"); return; }
    setBusy("add-sw");
    try {
      const { data } = await api.post(
        `/wordfence/clients/${clientId}/software`, {
          slug: newSw.slug.trim(),
          type: newSw.type,
          version: newSw.version.trim() || null,
          name: newSw.name.trim() || null,
          property_url: newSw.property_url.trim() || null,
        });
      setSoftware(data.items || []);
      setNewSw({ slug: "", type: "plugin", version: "", name: "", property_url: "" });
      toast.success("Added");
      // Re-run matching to pick up new item's vulnerabilities.
      const { data: mt } = await api.get(`/wordfence/clients/${clientId}/matches`);
      setMatches(mt.matches || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally { setBusy(null); }
  };

  const removeSoftware = async (item) => {
    if (!window.confirm(`Remove ${item.slug} from this client's registry?`)) return;
    try {
      const { data } = await api.delete(
        `/wordfence/clients/${clientId}/software/${item.item_id}`);
      setSoftware(data.items || []);
      const { data: mt } = await api.get(`/wordfence/clients/${clientId}/matches`);
      setMatches(mt.matches || []);
      toast.success("Removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm("Delete this event row?")) return;
    try {
      await api.delete(`/wordfence/events/${ev.event_id}`);
      setEvents((rows) => rows.filter((r) => r.event_id !== ev.event_id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const noKey = masterState && !masterState.configured;
  const noCache = masterState && (masterState.vulnerabilities_cached || 0) === 0;

  return (
    <div className="space-y-6" data-testid="client-wordfence-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#B22222]/10 grid place-items-center">
            <ShieldAlert className="h-5 w-5 text-[#B22222]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              Wordfence Security
            </h1>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Live security events pushed by this client&apos;s Wordfence
              install + vulnerable-plugin matching against the master
              Intelligence feed.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}
          className="rounded-sm" data-testid="wordfence-reload-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                   : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Reload
        </Button>
      </div>

      {(noKey || noCache) && (
        <Card className="rounded-sm border-amber-300 bg-amber-50"
          data-testid="wordfence-no-cache-warning">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[12px] text-amber-900 leading-relaxed">
              {noKey ? (
                <><strong>Wordfence API token not configured.</strong>{" "}
                  Vulnerability matching won&apos;t work until an admin sets
                  the master token on{" "}
                  <a href="/integrations/wordfence"
                    className="underline font-semibold hover:text-amber-950">
                    Integrations → Wordfence
                  </a>.</>
              ) : (
                <><strong>Vulnerability cache is empty.</strong>{" "}
                  Head to{" "}
                  <a href="/integrations/wordfence"
                    className="underline font-semibold hover:text-amber-950">
                    Integrations → Wordfence
                  </a>{" "}
                  and click <strong>Sync feed now</strong> to populate it.
                  Webhook events will still work.</>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Webhook setup */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-zinc-500" />
            <div className="label-overline">Webhook endpoint · paste this into Wordfence</div>
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono ml-auto">
              {webhook?.event_count || 0} events received
            </Badge>
          </div>
          {webhook ? (
            <>
              <div className="flex gap-2 flex-wrap">
                <Input readOnly value={webhook.webhook_url}
                  className="rounded-sm font-mono text-[11px] flex-1 min-w-[300px] bg-zinc-50"
                  data-testid="wordfence-webhook-url" />
                <Button size="sm" variant="outline" onClick={copyWebhook}
                  className="rounded-sm"
                  data-testid="wordfence-copy-webhook-btn">
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                </Button>
                <Button size="sm" variant="outline" onClick={regenerateWebhook}
                  disabled={busy === "regen"}
                  className="rounded-sm text-red-600 hover:bg-red-50"
                  data-testid="wordfence-regen-webhook-btn">
                  {busy === "regen"
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <><RotateCw className="h-3.5 w-3.5 mr-1" /> Regenerate</>}
                </Button>
              </div>
              <div className="text-[11px] text-zinc-600 leading-relaxed border-l-2 border-zinc-200 pl-3">
                <strong>How to wire it up (once per client):</strong>
                <ol className="mt-1 list-decimal pl-5 space-y-1">
                  <li>Log in to the client&apos;s WP admin → <em>Wordfence</em> plugin.</li>
                  <li>Under <em>Global Options</em> → <em>Alert Preferences</em> (or
                    the API Callbacks section on Wordfence Central) paste the URL
                    above as the webhook target.</li>
                  <li>Choose the events to forward: scan results, firewall blocks,
                    login attempts, alerts.</li>
                  <li>Save. Events start flowing into the chart below within a
                    minute of the first trigger.</li>
                </ol>
                <div className="mt-2 text-zinc-500">
                  Full setup guide:{" "}
                  <a href="https://www.wordfence.com/help/api-callbacks/"
                    target="_blank" rel="noreferrer"
                    className="text-[#B22222] hover:underline inline-flex items-center gap-0.5">
                    wordfence.com/help/api-callbacks
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                </div>
              </div>
            </>
          ) : (
            <div className="text-[12px] text-zinc-500">Loading webhook…</div>
          )}
        </CardContent>
      </Card>

      {/* Vulnerable software matches */}
      {matches.length > 0 && (
        <Card className="rounded-sm border-red-200 bg-red-50/40">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-red-700" />
              <div className="label-overline text-red-900">
                {matches.length} vulnerable plugin{matches.length === 1 ? "" : "s"}/theme{matches.length === 1 ? "" : "s"} detected
              </div>
            </div>
            <div className="space-y-3">
              {matches.map((m, i) => (
                <div key={`${m.vuln_id}-${m.item_id}`}
                  className="rounded-sm border border-red-200 bg-white p-3"
                  data-testid={`wordfence-match-${i}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-semibold text-[13px]">
                          {m.slug}
                        </span>
                        {m.version && (
                          <Badge className="rounded-sm text-[9px] font-mono bg-zinc-100 text-zinc-700">
                            v{m.version}
                          </Badge>
                        )}
                        <Badge className={`rounded-sm text-[9px] font-mono ${severityColor(m.cvss_rating)}`}>
                          {m.cvss_rating || "—"} · CVSS {m.cvss_score ?? "—"}
                        </Badge>
                        {m.cve && (
                          <a href={m.cve_link || "#"} target="_blank" rel="noreferrer"
                            className="text-[10px] font-mono text-red-700 hover:underline">
                            {m.cve}
                          </a>
                        )}
                        {m.informational && (
                          <Badge variant="outline" className="rounded-sm text-[9px] font-mono">
                            informational
                          </Badge>
                        )}
                      </div>
                      <div className="text-[12px] text-zinc-800 mt-1">{m.title}</div>
                      {m.remediation && (
                        <div className="text-[11px] text-zinc-600 mt-1 leading-relaxed">
                          <strong>Fix:</strong> {m.remediation}
                        </div>
                      )}
                      {(m.patched_versions || []).length > 0 && (
                        <div className="text-[10px] font-mono text-emerald-700 mt-1">
                          Patched in: {m.patched_versions.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Software registry */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="label-overline">Registered plugins &amp; themes</div>
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
              {software.length}
            </Badge>
          </div>
          {software.length === 0 ? (
            <div className="text-center py-6 text-[12px] text-zinc-500 border border-dashed border-zinc-300 rounded-sm">
              No plugins / themes registered yet. Add them below so we can flag
              vulnerabilities from the Wordfence Intelligence feed.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left border-b border-zinc-200">
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Slug</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Type</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Version</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Name</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Property URL</th>
                    <th className="py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {software.map((s) => {
                    const vulnerable = matches.some((m) => m.item_id === s.item_id);
                    return (
                      <tr key={s.item_id} className="border-b border-zinc-100 hover:bg-zinc-50"
                        data-testid={`wordfence-sw-row-${s.item_id}`}>
                        <td className="py-1.5 font-mono">
                          {s.slug}
                          {vulnerable && (
                            <Badge className="ml-2 rounded-sm text-[8px] font-mono bg-red-100 text-red-800">
                              VULN
                            </Badge>
                          )}
                        </td>
                        <td className="py-1.5 font-mono">{s.type}</td>
                        <td className="py-1.5 font-mono">{s.version || "—"}</td>
                        <td className="py-1.5">{s.name || "—"}</td>
                        <td className="py-1.5 font-mono text-zinc-500 truncate max-w-[200px]">
                          {s.property_url || "—"}
                        </td>
                        <td className="py-1.5 text-right">
                          <button onClick={() => removeSoftware(s)}
                            className="text-red-600 hover:bg-red-50 rounded-sm p-1"
                            data-testid={`wordfence-remove-sw-${s.item_id}`}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="pt-2 border-t border-zinc-100">
            <div className="label-overline mb-2">Add plugin / theme</div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
              <Input value={newSw.slug}
                onChange={(e) => setNewSw({ ...newSw, slug: e.target.value })}
                placeholder="slug (e.g. contact-form-7)"
                className="rounded-sm font-mono text-[12px] md:col-span-2"
                data-testid="wordfence-new-slug" />
              <select value={newSw.type}
                onChange={(e) => setNewSw({ ...newSw, type: e.target.value })}
                className="rounded-sm border border-zinc-200 px-2 text-[12px] font-mono bg-white"
                data-testid="wordfence-new-type">
                <option value="plugin">plugin</option>
                <option value="theme">theme</option>
                <option value="core">core</option>
              </select>
              <Input value={newSw.version}
                onChange={(e) => setNewSw({ ...newSw, version: e.target.value })}
                placeholder="version"
                className="rounded-sm font-mono text-[12px]" />
              <Input value={newSw.name}
                onChange={(e) => setNewSw({ ...newSw, name: e.target.value })}
                placeholder="display name"
                className="rounded-sm text-[12px]" />
              <Button onClick={addSoftware} disabled={busy === "add-sw"}
                className="rounded-sm bg-[#B22222] hover:bg-[#8B0000] text-white"
                data-testid="wordfence-add-sw-btn">
                {busy === "add-sw"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <><Plus className="h-3.5 w-3.5 mr-1" /> Add</>}
              </Button>
            </div>
            <Input value={newSw.property_url}
              onChange={(e) => setNewSw({ ...newSw, property_url: e.target.value })}
              placeholder="Optional: which property URL this is installed on"
              className="rounded-sm font-mono text-[12px] mt-2" />
          </div>
        </CardContent>
      </Card>

      {/* Events chart */}
      {daily.length > 0 && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-zinc-500" />
              <div className="label-overline">Wordfence events · daily</div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={daily}
                margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#71717a" }} />
                <YAxis tick={{ fontSize: 10, fill: "#71717a" }} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                <Bar dataKey="count" fill="#B22222" name="events" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent events table */}
      {events.length > 0 && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="label-overline mb-3">
              Recent events · {events.length} row(s)
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left border-b border-zinc-200">
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">When</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Event</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Severity</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Host</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Message</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">Metric</th>
                    <th className="py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.event_id} className="border-b border-zinc-100 hover:bg-zinc-50"
                      data-testid={`wordfence-event-row-${e.event_id}`}>
                      <td className="py-1.5 font-mono text-zinc-700">
                        {new Date(e.received_at).toLocaleString(undefined,
                          { day: "numeric", month: "short", year: "2-digit",
                            hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="py-1.5 font-mono">{e.event_type}</td>
                      <td className="py-1.5">
                        <Badge className={`rounded-sm text-[9px] font-mono ${severityColor(e.severity)}`}>
                          {e.severity || "—"}
                        </Badge>
                      </td>
                      <td className="py-1.5 font-mono text-zinc-500 truncate max-w-[160px]">
                        {e.host || "—"}
                      </td>
                      <td className="py-1.5 truncate max-w-md">{e.message || "—"}</td>
                      <td className="py-1.5 font-mono text-center">{e.metric ?? "—"}</td>
                      <td className="py-1.5 text-right">
                        <button onClick={() => deleteEvent(e)}
                          className="text-red-600 hover:bg-red-50 rounded-sm p-1"
                          data-testid={`wordfence-delete-event-${e.event_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {events.length === 0 && webhook && (
        <Card className="rounded-sm border-zinc-200 bg-zinc-50/60">
          <CardContent className="p-5 text-center text-[12px] text-zinc-500">
            No events received yet. Once the webhook URL above is pasted into
            the client&apos;s Wordfence dashboard, blocked attacks / scan
            results / alerts will appear here in real-time.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
